import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { WaitlistForm } from '@/components/WaitlistForm';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  Check,
  Zap,
  Star,
  Building2,
  Sparkles,
  MousePointer2,
  Bot,
  Handshake,
  Plug,
  ArrowRight,
  Package,
  BarChart3,
  Users,
  Brain,
  Shield,
  CreditCard,
} from 'lucide-react';
import heroImage from '@/assets/hero-antiques.jpg';
import { usePageMeta } from '@/hooks/usePageMeta';
import { JsonLd } from '@/components/seo/JsonLd';
import { FosButton, FosBadge } from '@/components/ui/flipperos';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildSoftwareApplicationSchema,
} from '@/components/seo/schemas';
import { PRICING_TIERS, type PricingTier, type PricingTierIconName } from '@/data/pricingTiers';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LandingProps {
  onSignIn: () => void;
  onStartFree: () => void;
  /** Optional override; when omitted, the demo button signs in directly with VITE_DEMO_* creds. */
  onTryDemo?: () => void;
}

const DEMO_EMAIL = import.meta.env['VITE_DEMO_EMAIL'] as string | undefined;
const DEMO_PASSWORD = import.meta.env['VITE_DEMO_PASSWORD'] as string | undefined;

// ─── Pricing data ────────────────────────────────────────────────────────────
// Tiers come from the single source of truth (src/data/pricingTiers.ts). That
// module stays JSX-free, so map its lucide icon *names* back to components here.

const TIER_ICONS: Record<PricingTierIconName, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Zap,
  Star,
  Building2,
};

// ─── Three-mode explainer data ────────────────────────────────────────────────

const MODES = [
  {
    icon: MousePointer2,
    name: 'Manual',
    tagline: 'You drive every action',
    description:
      'Classic UI for sourcing runs, manual review, and edge cases. Full control, no surprises.',
    tier: 'All tiers',
    color: 'text-fos-text-muted',
  },
  {
    icon: Handshake,
    name: 'Co-Pilot',
    tagline: 'AI suggests, you approve',
    description:
      'Every listing, repricing, and message reply comes with a clear diff and one-click Approve / Reject / Edit.',
    tier: 'Pro+',
    color: 'text-fos-accent',
  },
  {
    icon: Bot,
    name: 'Autonomous',
    tagline: 'Agents act within your guardrails',
    description:
      'Set rules once — "reprice items older than 30 days, never below cost+15%" — and get a daily digest with one-click rollback.',
    tier: 'Power+',
    color: 'text-fos-accent',
  },
];

// ─── MCP tool surface (from PRODUCT_VISION_V2.md §2.1) ───────────────────────

const MCP_TOOLS = [
  { icon: Package, label: 'list_inventory' },
  { icon: BarChart3, label: 'get_metrics' },
  { icon: Users, label: 'list_customers' },
  { icon: Zap, label: 'generate_listing' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function ModeCard({ mode }: { mode: (typeof MODES)[number] }) {
  const Icon = mode.icon;
  return (
    <Card className="flex h-full flex-col border-fos-border bg-fos-bg-card transition-colors hover:border-fos-accent-border">
      <CardHeader className="pb-2">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fos-accent-bg">
            <Icon className={`h-5 w-5 ${mode.color}`} />
          </div>
          <FosBadge variant="secondary" className="text-xs">
            {mode.tier}
          </FosBadge>
        </div>
        <h3 className="text-xl font-bold">{mode.name}</h3>
        <p className="text-sm font-medium text-fos-accent">{mode.tagline}</p>
      </CardHeader>
      <CardContent>
        <p className="text-sm leading-relaxed text-fos-text-muted">{mode.description}</p>
      </CardContent>
    </Card>
  );
}

function PricingCard({
  tier,
  annual,
  onCta,
}: {
  tier: PricingTier;
  annual: boolean;
  onCta: () => void;
}) {
  const Icon = TIER_ICONS[tier.iconName];
  const price = annual ? tier.annualPrice : tier.monthlyPrice;
  const period = tier.monthlyPrice === 0 ? 'forever free' : annual ? '/ year' : '/ month';
  const savings = annual && tier.monthlyPrice > 0 ? `Save $${tier.monthlyPrice * 2}/yr` : null;

  return (
    <Card
      className={`relative flex flex-col ${
        tier.highlight
          ? 'shadow-fos-accent/10 border-fos-accent bg-fos-bg-card shadow-lg ring-1 ring-fos-accent'
          : 'border-fos-border bg-fos-bg-card'
      }`}
    >
      {tier.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <FosBadge className="bg-fos-accent px-3 text-xs text-fos-text-inverse">
            {tier.badge}
          </FosBadge>
        </div>
      )}
      <CardHeader className="pb-2 pt-6">
        <div className="mb-2 flex items-center gap-2">
          <Icon
            className={`h-5 w-5 ${tier.highlight ? 'text-fos-accent' : 'text-fos-text-muted'}`}
          />
          <span className="text-lg font-semibold">{tier.name}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">${price}</span>
          <span className="text-sm text-fos-text-muted">{period}</span>
        </div>
        {savings && <p className="text-xs font-medium text-fos-accent">{savings}</p>}
        <p className="mt-1 text-sm text-fos-text-muted">{tier.tagline}</p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 pt-0">
        <FosButton
          variant={tier.highlight ? 'default' : 'outline'}
          className="w-full"
          onClick={onCta}
        >
          {tier.cta}
        </FosButton>
        <ul className="space-y-2">
          {tier.perks.map((perk) => (
            <li key={perk} className="flex items-center gap-2 text-sm text-fos-text-muted">
              <Check className="h-3.5 w-3.5 flex-shrink-0 text-fos-accent" />
              {perk}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function Landing({ onSignIn, onStartFree, onTryDemo }: LandingProps) {
  const [annual, setAnnual] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  usePageMeta({
    title: 'AI Command Center for Resellers',
    description:
      'List once. Sell everywhere. When it sells anywhere, we delist everywhere — automatically. AI drafts your listings, you approve them.',
  });

  const handleTryDemo = async () => {
    if (onTryDemo) {
      onTryDemo();
      return;
    }
    if (!DEMO_EMAIL || !DEMO_PASSWORD) {
      toast({
        title: 'Demo unavailable',
        description:
          'Demo credentials are not configured. Set VITE_DEMO_EMAIL and VITE_DEMO_PASSWORD.',
        variant: 'destructive',
      });
      return;
    }
    setDemoLoading(true);
    try {
      await login(DEMO_EMAIL, DEMO_PASSWORD);
      navigate('/');
    } catch (err) {
      toast({
        title: 'Demo sign-in failed',
        description: err instanceof Error ? err.message : 'Could not sign in to the demo account.',
        variant: 'destructive',
      });
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fos-bg-base text-fos-text-primary">
      <JsonLd data={buildOrganizationSchema()} />
      <JsonLd data={buildWebSiteSchema()} />
      <JsonLd data={buildSoftwareApplicationSchema()} />
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <header className="bg-fos-bg-base/95 supports-[backdrop-filter]:bg-fos-bg-base/60 sticky top-0 z-50 border-b border-fos-border backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <Logo variant="wordmark" size="md" scheme="light" />
          <div className="flex items-center gap-2">
            <FosButton variant="ghost" size="sm" onClick={onSignIn}>
              Sign in
            </FosButton>
            <FosButton size="sm" onClick={onStartFree}>
              Start free
            </FosButton>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            loading="lazy"
            src={heroImage}
            alt=""
            aria-hidden
            className="h-full w-full object-cover opacity-10"
          />
          <div className="gradient-hero absolute inset-0 opacity-30" />
        </div>
        <div className="mx-auto max-w-4xl space-y-6 px-4 py-24 text-center">
          <FosBadge
            variant="secondary"
            className="mb-2 border-fos-accent-border bg-fos-accent-bg text-fos-accent"
          >
            AI-native · eBay-first · MCP-ready
          </FosBadge>
          <h1 className="text-5xl font-bold leading-tight tracking-tight sm:text-6xl">
            List once.
            <br />
            <span className="text-fos-accent">Sell everywhere.</span>
          </h1>
          <p className="mx-auto max-w-2xl text-xl leading-relaxed text-fos-text-muted">
            When it sells anywhere, we delist everywhere — automatically. AI drafts your listings,
            you approve them, and our agents handle the rest.
          </p>
          <div className="flex flex-col items-center gap-4 pt-2">
            <WaitlistForm />
            <FosButton size="md" variant="outline" onClick={handleTryDemo} disabled={demoLoading}>
              {demoLoading ? 'Signing in…' : 'Try demo account'}
            </FosButton>
          </div>
          <p className="text-xs text-fos-text-muted">
            No credit card required · Free forever plan · Cancel any time
          </p>
        </div>
      </section>

      {/* ── Three modes ─────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl space-y-10 px-4 py-20">
        <div className="space-y-3 text-center">
          <h2 className="text-3xl font-bold">Three ways to work</h2>
          <p className="mx-auto max-w-xl text-fos-text-muted">
            Start manual, graduate to Co-Pilot, unlock Autonomous — upgrade at your pace.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {MODES.map((mode) => (
            <ModeCard key={mode.name} mode={mode} />
          ))}
        </div>
      </section>

      {/* ── MCP integration callout ──────────────────────────────────────── */}
      <section className="bg-accent text-accent-foreground">
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-16">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-fos-accent-bg-strong">
              <Plug className="h-5 w-5 text-fos-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">MCP-native from day one</h2>
              <p className="text-sm text-accent-foreground/70">
                Use For Visibility from Claude Desktop, Cursor, or any MCP-capable AI client
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2">
            <div className="space-y-4">
              <p className="leading-relaxed text-accent-foreground/80">
                For Visibility ships a first-class{' '}
                <code className="rounded bg-fos-accent-bg px-1.5 py-0.5 font-mono text-sm text-fos-accent">
                  @forvisibility/mcp-server
                </code>{' '}
                that exposes your inventory, sales, customers, and pricing as tools to any
                MCP-capable AI client.
              </p>
              <p className="leading-relaxed text-accent-foreground/80">
                Ask Claude: <em>"What's my dead inventory?"</em> or{' '}
                <em>"Draft a listing for the Royal Doulton vase I just intaked."</em> Claude reads
                and writes your For Visibility data directly — no copy-paste.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                {MCP_TOOLS.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-1.5 rounded-md bg-fos-accent-bg px-2.5 py-1 font-mono text-xs text-fos-accent"
                  >
                    <Icon className="h-3 w-3" />
                    {label}()
                  </div>
                ))}
                <div className="flex items-center gap-1.5 rounded-md bg-fos-accent-bg px-2.5 py-1 font-mono text-xs text-fos-accent">
                  + 18 more
                </div>
              </div>
            </div>
            <div className="space-y-2 rounded-lg border border-accent-foreground/10 bg-accent-foreground/5 p-4 font-mono text-sm">
              <div className="mb-3 text-xs text-accent-foreground/40">
                Claude Desktop · @forvisibility/mcp-server
              </div>
              <div>
                <span className="text-fos-accent">user</span>
                <span className="text-accent-foreground/50"> › </span>
                <span className="text-accent-foreground">What's my dead inventory?</span>
              </div>
              <div className="pl-4 text-xs text-accent-foreground/50">
                ↳ list_inventory({'{'} max_age_days: 30 {'}'})
              </div>
              <div className="pl-4 text-xs leading-relaxed text-accent-foreground/70">
                Found 12 items older than 30 days with no sales activity. Top candidates: 1970s
                ceramic lamp ($45 cost, $52 list), set of 6 crystal glasses ($30 cost, $38 list)…
              </div>
            </div>
          </div>
          <p className="text-xs text-accent-foreground/50">
            Available on Pro (read-only) and Power+ (read + write) · MIT-licensed open-source server
            · PAT tokens scoped per-tool
          </p>
        </div>
      </section>

      {/* ── Trust & Transparency ──────────────────────────────────────────── */}
      <section className="bg-muted/30">
        <div className="mx-auto max-w-6xl space-y-12 px-4 py-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold">You're always in control</h2>
            <p className="mt-2 text-fos-text-muted">
              Every AI action is logged. Every action is undoable.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="space-y-3 rounded-xl border bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fos-accent-bg">
                <Brain className="h-5 w-5 text-fos-accent" />
              </div>
              <h3 className="text-lg font-semibold">Co-Pilot mode</h3>
              <p className="text-sm text-fos-text-muted">
                AI suggests — you approve, edit, or reject. Every draft, every repricing, every
                message reply comes with a clear diff before it goes live.
              </p>
            </div>
            <div className="space-y-3 rounded-xl border bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fos-accent-bg">
                <Shield className="h-5 w-5 text-fos-accent" />
              </div>
              <h3 className="text-lg font-semibold">30-day undo on everything</h3>
              <p className="text-sm text-fos-text-muted">
                Every agent action — listings created, prices changed, messages sent — is logged in
                your audit trail. Undo any action within 30 days with one click.
              </p>
            </div>
            <div className="space-y-3 rounded-xl border bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fos-accent-bg">
                <CreditCard className="h-5 w-5 text-fos-accent" />
              </div>
              <h3 className="text-lg font-semibold">Tax exports built in</h3>
              <p className="text-sm text-fos-text-muted">
                Schedule-C and 1099-K reports generated automatically from your sales data. No extra
                app, no accountant confusion — included on every paid plan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl space-y-10 px-4 py-20">
        <div className="space-y-3 text-center">
          <h2 className="text-3xl font-bold">Reseller-friendly pricing</h2>
          <p className="mx-auto max-w-xl text-fos-text-muted">
            No cuts of your sales. No per-listing fees. Cancel any time with full data export.
          </p>
          <div className="mt-2 inline-flex items-center gap-3 rounded-full bg-fos-bg-elevated px-4 py-2">
            <button
              onClick={() => setAnnual(false)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                !annual
                  ? 'bg-fos-bg-base text-fos-text-primary shadow'
                  : 'text-fos-text-muted hover:text-fos-text-primary'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                annual
                  ? 'bg-fos-bg-base text-fos-text-primary shadow'
                  : 'text-fos-text-muted hover:text-fos-text-primary'
              }`}
            >
              Annual
            </button>
            {annual && (
              <FosBadge
                variant="secondary"
                className="border-fos-accent-border bg-fos-accent-bg text-xs text-fos-accent"
              >
                2 months free
              </FosBadge>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PRICING_TIERS.map((tier) => (
            <PricingCard
              key={tier.id}
              tier={tier}
              annual={annual}
              onCta={tier.id === 'free' ? onStartFree : onSignIn}
            />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 text-center text-sm text-fos-text-muted sm:grid-cols-3">
          {[
            {
              title: 'No cuts of your sales',
              body: "eBay and Mercari already take theirs. We don't.",
            },
            {
              title: 'Cancel any time',
              body: 'Full CSV + JSON data export included. Your data is yours.',
            },
            {
              title: 'No surprise overages',
              body: 'Soft caps with clear upgrade prompts — never a surprise bill.',
            },
          ].map(({ title, body }) => (
            <div key={title} className="space-y-1">
              <p className="font-medium text-fos-text-primary">{title}</p>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Demo CTA ────────────────────────────────────────────────────── */}
      <section className="bg-fos-bg-elevated/40 border-t border-fos-border">
        <div className="mx-auto max-w-2xl space-y-5 px-4 py-16 text-center">
          <h2 className="text-3xl font-bold">See it in action</h2>
          <p className="text-fos-text-muted">
            {/* The real demo account comes from VITE_DEMO_EMAIL (fl-aae); a
                hardcoded address here drifted from it. */}
            Log in as{' '}
            {DEMO_EMAIL ? (
              <code className="rounded bg-fos-bg-elevated px-1.5 py-0.5 text-sm text-fos-text-primary">
                {DEMO_EMAIL}
              </code>
            ) : (
              'the demo account'
            )}{' '}
            to explore a pre-loaded store — 50 items, 20 customers, 30 sales, real eBay listings. No
            sign-up required.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <FosButton size="md" onClick={handleTryDemo} disabled={demoLoading} className="gap-2">
              {demoLoading ? 'Signing in…' : 'Try demo account'}
              {!demoLoading && <ArrowRight className="h-4 w-4" />}
            </FosButton>
            <FosButton size="md" variant="outline" onClick={onStartFree}>
              Start free instead
            </FosButton>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-fos-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 text-sm text-fos-text-muted sm:flex-row">
          <div className="flex items-center gap-2">
            <Logo variant="wordmark" size="sm" scheme="light" />
            <span>· AI-native reseller OS</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="transition-colors hover:text-fos-text-primary">
              Pricing
            </Link>
            <Link to="/roadmap" className="transition-colors hover:text-fos-text-primary">
              Roadmap
            </Link>
            <Link to="/compare/vendoo" className="transition-colors hover:text-fos-text-primary">
              Compare
            </Link>
            <Link to="/legal/terms" className="transition-colors hover:text-fos-text-primary">
              Terms
            </Link>
            <Link to="/legal/privacy" className="transition-colors hover:text-fos-text-primary">
              Privacy
            </Link>
            <Link to="/legal/security" className="transition-colors hover:text-fos-text-primary">
              Security
            </Link>
            <button onClick={onSignIn} className="transition-colors hover:text-fos-text-primary">
              Sign in
            </button>
            <button onClick={onStartFree} className="transition-colors hover:text-fos-text-primary">
              Start free
            </button>
            <button
              onClick={handleTryDemo}
              disabled={demoLoading}
              className="transition-colors hover:text-fos-text-primary disabled:opacity-50"
            >
              {demoLoading ? 'Signing in…' : 'Try demo'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
